import type { CardServiceFeeQuote } from '@/hooks/payments/useCardServiceFeeQuote';
import { cn } from '@/lib/utils';
import { resolveChargeTotals } from './chargeTotals';

function formatCurrency(amount: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
}

/**
 * How base, discount, tip and fee add up to what the customer is charged.
 *
 * `ChargeServiceFeePreview` speaks about the base alone, which is the whole
 * story until a tip rides on top of it — then the operator reads a card price
 * that is not what leaves the account. Only rendered when there is a tip;
 * without one the preview already states the total.
 */
export function ChargeTotalBreakdown({
  quote,
  baseAmount,
  tipAmount = 0,
  currency = 'USD',
  className,
  'data-testid': testId = 'charge-total-breakdown',
}: {
  quote?: CardServiceFeeQuote | null;
  baseAmount: number;
  tipAmount?: number;
  currency?: string;
  className?: string;
  'data-testid'?: string;
}) {
  const tip = Number.isFinite(tipAmount) && tipAmount > 0 ? tipAmount : 0;
  if (!(tip > 0)) return null;
  const { discountAmount, feeAmount, chargeTotal } = resolveChargeTotals(quote, baseAmount, tip);
  return (
    <p className={cn('text-xs text-muted-foreground', className)} data-testid={testId}>
      {formatCurrency(baseAmount, currency)}
      {discountAmount > 0
        ? ` − ${formatCurrency(discountAmount, currency)} ACH/check discount`
        : ''}{' '}
      + {formatCurrency(tip, currency)} tip
      {feeAmount > 0 ? ` + ${formatCurrency(feeAmount, currency)} card price` : ''} ={' '}
      {formatCurrency(chargeTotal, currency)}
    </p>
  );
}
